import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../constants';
import {
  SummaryWidgetsResponse,
  useAsyncAction,
  useStoreInformation,
  useTransaction,
  useUser,
} from '../../../hooks';
import { Icons } from '../../../stories/atoms';
import { muiTheme } from '../../../theme';
import { areaOverviewTranslations } from '../../../translations/areaOverview';
import { isFailureResponse } from '../../../utils';
import { BudgetPlanOverviewWidget } from '../../home/widgets/areaOverviewWidgets/budgetPlanOverviewWidget/BudgetPlanOverviewWidget';
import { ConversionRateOverviewWidget } from '../../home/widgets/areaOverviewWidgets/conversionRateOverviewWidget/ConversionRateOverviewWidget';
import { EfficiencyOverviewWidget } from '../../home/widgets/areaOverviewWidgets/efficiencyOverviewWidget/EfficiencyOverviewWidget';
import { KpiOverviewWidget } from '../../home/widgets/areaOverviewWidgets/kpiOverviewWidget/KpiOverviewWidget';
import { MemberOverviewWidget } from '../../home/widgets/areaOverviewWidgets/memberOverviewWidget/MemberOverviewWidget';
import { QueueCustomerOverviewWidget } from '../../home/widgets/areaOverviewWidgets/queueCustomerOverviewWidget/QueueCustomerOverviewWidget';
import { RFIDOverviewWidget } from '../../home/widgets/areaOverviewWidgets/rfidOverviewWidget/RFIDOverviewWidget';
import { SalesOverviewWidget } from '../../home/widgets/areaOverviewWidgets/salesOverviewWidget/SalesOverviewWidget';
import { SummaryWidget } from '../summaryWidget/SummaryWidget';
import { AreaWidgetType, getIsAreaWidgetVisible } from '../util';
import { AreaWidgetErrorBoundary } from '../fallbackComponent/AreaWidgetErrorBoundary';
import { OverviewWidgetsContainer, StyledContainer, SummaryWidgetsContainer } from './AreaWidgetsContainer.styles';
import { useFetchAreaWidgetsData } from './useFetchAreaWidgetsData';

export type AreaWidgetsContainerProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
};
export const AreaWidgetsContainer: FC<AreaWidgetsContainerProps> = observer(({ currentDate, interval }) => {
  const {
    get: { userPermissions },
  } = useUser();
  const { t } = useTranslation();
  const { summaryWidgetsOverview } = areaOverviewTranslations;

  const { corporateBrandId } = useStoreInformation();

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));

  const { isAreaWidgetVisible } = getIsAreaWidgetVisible({
    userPermissions,
  });

  const {
    conversionRateOverviewData,
    conversionRateOverviewLoading,
    conversionRateOverviewError,
    fetchConversionRateOverviewWidgetData,

    salesOverviewData,
    salesOverviewLoading,
    salesOverviewError,
    fetchSalesOverviewWidgetData,

    membersOverviewData,
    membersOverviewLoading,
    membersOverviewError,
    fetchMemberOverviewWidgetData,

    queueCustomerOverviewData,
    queueCustomerOverviewLoading,
    queueCustomerOverviewError,
    fetchQueueCustomerOverviewWidgetData,

    rfidOverviewData,
    rfidOverviewLoading,
    rfidOverviewError,
    fetchRfidOverviewWidgetData,
  } = useFetchAreaWidgetsData({ interval, currentDate });

  const { fetchSummaryWidgetsData } = useTransaction();

  const {
    result: summaryWidgetsData,
    isLoading: summaryWidgetsLoading,
    isError: summaryWidgetsError,
    triggerAction: triggerFetchSummaryWidgets,
  } = useAsyncAction<SummaryWidgetsResponse>();

  const fetchData = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchSummaryWidgetsData(request);
    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  const fetchSummaryData = () => {
    triggerFetchSummaryWidgets(fetchData);
  };

  useEffect(() => {
    fetchSummaryData();
  }, [currentDate]);

  const summaryWidgets = () => {
    return [
      {
        id: 'kpi',
        title: t(summaryWidgetsOverview.kpis.key, summaryWidgetsOverview.kpis.defaultValue),
        description: t(
          summaryWidgetsOverview.kpisDescription.key,
          summaryWidgetsOverview.kpisDescription.defaultValue,
        ),
        headerIcon: Icons.TARGET,
        value: summaryWidgetsData?.kpis ?? 0,
      },
      {
        id: 'totalTransactions',
        title: t(
          summaryWidgetsOverview.totalTransactions.key,
          summaryWidgetsOverview.totalTransactions.defaultValue,
        ),
        description: t(
          summaryWidgetsOverview.totalTransactionsDescription.key,
          summaryWidgetsOverview.totalTransactionsDescription.defaultValue,
        ),
        headerIcon: Icons.TRANSACTION_SEARCH,
        value: summaryWidgetsData?.totalTransactions ?? 0,
      },
      ...(corporateBrandId === 0
        ? [
            {
              id: 'newMembers',
              title: t(
                summaryWidgetsOverview.totalNewMembers.key,
                summaryWidgetsOverview.totalNewMembers.defaultValue,
              ),
              description: t(
                summaryWidgetsOverview.totalNewMembersDescription.key,
                summaryWidgetsOverview.totalNewMembersDescription.defaultValue,
              ),
              headerIcon: Icons.NEWMEMBER,
              value: summaryWidgetsData?.totalNewMembers ?? 0,
            },
          ]
        : [
            {
              id: 'discountedTransactions',
              title: t(
                summaryWidgetsOverview.totalDiscountedTransactions.key,
                summaryWidgetsOverview.totalDiscountedTransactions.defaultValue,
              ),
              description: t(
                summaryWidgetsOverview.totalDiscountedTransactionsDescription.key,
                summaryWidgetsOverview.totalDiscountedTransactionsDescription.defaultValue,
              ),
              headerIcon: Icons.CONVERSION,
              value: summaryWidgetsData?.totalDiscountedTransactions ?? 0,
            },
          ]),
      {
        id: 'totalSoldPieces',
        title: t(summaryWidgetsOverview.totalSoldPieces.key, summaryWidgetsOverview.totalSoldPieces.defaultValue),
        description: t(
          summaryWidgetsOverview.totalSoldPiecesDescription.key,
          summaryWidgetsOverview.totalSoldPiecesDescription.defaultValue,
        ),
        headerIcon: Icons.BAG,
        value: summaryWidgetsData?.totalSoldPieces ?? 0,
      },
      {
        id: 'totalReturnedPieces',
        title: t(
          summaryWidgetsOverview.totalReturnedPieces.key,
          summaryWidgetsOverview.totalReturnedPieces.defaultValue,
        ),
        description: t(
          summaryWidgetsOverview.totalReturnedPiecesDescription.key,
          summaryWidgetsOverview.totalReturnedPiecesDescription.defaultValue,
        ),
        headerIcon: Icons.RETURNS,
        value: summaryWidgetsData?.totalReturnedPieces ?? 0,
      },
    ];
  };

  useEffect(() => {
    const { isAreaWidgetVisible: isAreaWidgetEnabled } = getIsAreaWidgetVisible({
      userPermissions,
    });

    if (isAreaWidgetEnabled(AreaWidgetType.ConversionRateOverview)) {
      fetchConversionRateOverviewWidgetData();
    }

    if (isAreaWidgetEnabled(AreaWidgetType.SalesOverview)) {
      fetchSalesOverviewWidgetData();
    }

    if (isAreaWidgetEnabled(AreaWidgetType.MembersOverview)) {
      fetchMemberOverviewWidgetData();
    }

    if (isAreaWidgetEnabled(AreaWidgetType.QueueCustomerOverview)) {
      fetchQueueCustomerOverviewWidgetData();
    }

    if (isAreaWidgetEnabled(AreaWidgetType.RfidOverview)) {
      fetchRfidOverviewWidgetData();
    }
  }, [userPermissions, currentDate, interval]);

  return (
    <StyledContainer data-testid="area-grid-container">
      <SummaryWidgetsContainer isMobile={isMobile} data-testid="summary-widgets-container">
        {summaryWidgets().map((widget) => (
          <SummaryWidget
            key={widget.id}
            id={widget.id}
            title={widget.title}
            description={widget.description}
            headerIcon={widget.headerIcon}
            value={widget.value}
            isLoading={summaryWidgetsLoading}
            error={summaryWidgetsError}
            retryHandler={fetchSummaryData}
          />
        ))}
      </SummaryWidgetsContainer>

      <OverviewWidgetsContainer
        isMobile={isMobile}
        isSmallDesktop={isSmallDesktop}
        data-testid="overview-widgets-container"
      >
        {isAreaWidgetVisible(AreaWidgetType.SalesOverview) && (
          <AreaWidgetErrorBoundary widgetName="SalesOverviewWidget">
            <SalesOverviewWidget
              interval={interval}
              currentDate={currentDate}
              data={salesOverviewData}
              isLoading={salesOverviewLoading}
              isError={salesOverviewError}
              errorRetryHandler={fetchSalesOverviewWidgetData}
            />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.BudgetPlanOverview) && (
          <AreaWidgetErrorBoundary widgetName="BudgetPlanOverviewWidget">
            <BudgetPlanOverviewWidget currentDate={currentDate} interval={interval} />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.ConversionRateOverview) && (
          <AreaWidgetErrorBoundary widgetName="ConversionRateOverviewWidget">
            <ConversionRateOverviewWidget
              data={conversionRateOverviewData}
              isLoading={conversionRateOverviewLoading}
              isError={conversionRateOverviewError}
              errorRetryHandler={fetchConversionRateOverviewWidgetData}
            />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.EfficiencyOverview) && (
          <AreaWidgetErrorBoundary widgetName="EfficiencyOverviewWidget">
            <EfficiencyOverviewWidget currentDate={currentDate} interval={interval} />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.MembersOverview) && (
          <AreaWidgetErrorBoundary widgetName="MemberOverviewWidget">
            <MemberOverviewWidget
              interval={interval}
              data={membersOverviewData}
              isLoading={membersOverviewLoading}
              isError={membersOverviewError}
              errorRetryHandler={fetchMemberOverviewWidgetData}
            />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.RfidOverview) && (
          <AreaWidgetErrorBoundary widgetName="RFIDOverviewWidget">
            <RFIDOverviewWidget
              data={rfidOverviewData}
              isLoading={rfidOverviewLoading}
              isError={rfidOverviewError}
              errorRetryHandler={fetchRfidOverviewWidgetData}
            />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.KPIForecastOverview) && (
          <AreaWidgetErrorBoundary widgetName="KpiOverviewWidget">
            <KpiOverviewWidget
              currentDate={currentDate}
              interval={interval}
              conversionRateOverviewData={conversionRateOverviewData}
              salesOverviewData={salesOverviewData}
              membersOverviewData={membersOverviewData}
              queueCustomerOverviewData={queueCustomerOverviewData}
              rfidOverviewData={rfidOverviewData}
              summaryWidgetsData={summaryWidgetsData}
            />
          </AreaWidgetErrorBoundary>
        )}

        {isAreaWidgetVisible(AreaWidgetType.QueueCustomerOverview) && (
          <AreaWidgetErrorBoundary widgetName="QueueCustomerOverviewWidget">
            <QueueCustomerOverviewWidget
              data={queueCustomerOverviewData}
              isLoading={queueCustomerOverviewLoading}
              isError={queueCustomerOverviewError}
              errorRetryHandler={fetchQueueCustomerOverviewWidgetData}
            />
          </AreaWidgetErrorBoundary>
        )}
      </OverviewWidgetsContainer>
    </StyledContainer>
  );
});
